import { DossierId } from '@/dossier/models/AbstractDossier';
import Identity from '@/general/models/Identity';
import ChatType, { ChatTypeVisitor, visitChatType } from '@/shared/constants/ChatType';
import Language from '@/shared/constants/Language';
import AbstractContext from '@/shared/models/AbstractContext';
import Snippet from '@/shared/models/Snippet';
import EventBus from '@/shared/utils/EventBus';
import ChatPage from '../constants/ChatPage';
import { ChatContentElement } from '../utils/parseChatContent';
import Chat from './Chat';
import ChatMessage from './ChatMessage';
import { ChatMessageAdded } from './chatEvents';

export default abstract class ChatContext extends AbstractContext {
  newContent = '';
  page = ChatPage.MESSAGES;
  internalMessageCount: number | null = null;
  externalMessageCount: number | null = null;
  snippets: Array<Snippet> = [];
  eventBus = new EventBus();

  protected constructor(
    readonly type: ChatType,
    readonly chat: Chat,
    readonly currentUser: Identity,
    readonly dossierId: DossierId,
  ) {
    super();
  }

  isInternal(): boolean {
    return this.type === ChatType.INTERNAL;
  }

  isExternal(): boolean {
    return this.type === ChatType.EXTERNAL;
  }

  countMessages(type: ChatType = this.type): number | null {
    if (type === this.type) return this.chat.messages.length;
    return visitChatType(type, {
      internal: () => this.internalMessageCount,
      external: () => this.externalMessageCount,
    });
  }

  canLinkMedia(): boolean {
    return this.isExternal();
  }

  visitType<Result>(visitor: ChatTypeVisitor<Result>): Result {
    return visitChatType(this.type, visitor);
  }

  abstract canWrite(): boolean;

  getSanitizedContent(): string {
    return this.newContent.trim();
  }

  canSend(): boolean {
    return this.getSanitizedContent().length > 0;
  }

  parseChatContent(content: string): Array<ChatContentElement> {
    return [content];
  }

  addMessage(message: ChatMessage, type = this.type): void {
    if (type === this.type) {
      this.chat.addMessage(message);
    }
    visitChatType(type, {
      internal: () => this.internalMessageCount !== null && this.internalMessageCount++,
      external: () => this.externalMessageCount !== null && this.externalMessageCount++,
    });
    this.eventBus.fire(new ChatMessageAdded(message, type));
  }

  afterSend(messages: Array<ChatMessage>): void {
    messages.forEach(m => this.addMessage(m));
    if (this.page !== ChatPage.MEDIA) this.newContent = '';
    this.resetPage();
  }

  getLanguages(): Array<Language> {
    return [this.currentUser.language];
  }

  resetPage(): void {
    this.page = ChatPage.MESSAGES;
  }
}